import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { BarChart2, Coins } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './DepositCalculatorPage/components/CalculationForm';
import ResultsOverview from './DepositCalculatorPage/components/ResultsOverview';
import DepositSchedule from './DepositCalculatorPage/components/DepositSchedule';
import QuickAdjustmentSidebar from './DepositCalculatorPage/components/QuickAdjustmentSidebar';
import ComparisonSidebar from './DepositCalculatorPage/components/ComparisonSidebar';
import TipsSidebar from './DepositCalculatorPage/components/TipsSidebar';
import InfoContent from './DepositCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  depositAmount: '300000',
  depositTerm: '12', // Месяцы
  interestRate: '8.5', // Годовой процент
  capitalization: 'monthly', // 'none', 'daily', 'monthly', 'quarterly', 'annually'
  startDate: new Date().toISOString().split('T')[0],
  depositType: 'standard',
  currency: 'RUB',
  tax: true,
  additionalContributions: false,
  contributionFrequency: 'monthly', // 'once', 'monthly', 'quarterly', 'annually'
  contributionAmount: '10000',
  contributionStartMonth: '2', // Месяц начала пополнений
  withdrawals: false,
  withdrawalFrequency: 'quarterly', // 'once', 'monthly', 'quarterly', 'annually'
  withdrawalAmount: '5000',
  withdrawalStartMonth: '3', // Месяц начала снятий
  earlyTermination: false,
  earlyTerminationMonth: '6', // Месяц досрочного закрытия
};

// Определение типов вкладов
export const depositTypes = [
  { id: 'standard', name: 'Стандартный', defaultRate: 8.5 },
  { id: 'profitable', name: 'Доходный', defaultRate: 9.2 },
  { id: 'comfort', name: 'Комфорт', defaultRate: 8.0 },
  { id: 'vip', name: 'VIP', defaultRate: 9.5 },
  { id: 'urgent', name: 'Срочный', defaultRate: 7.8 },
  { id: 'savings', name: 'Накопительный', defaultRate: 7.5 }
];

// Определение валют
export const currencyOptions = [
  { id: 'RUB', name: 'Рубли', symbol: '₽' },
  { id: 'USD', name: 'Доллары', symbol: '$' },
  { id: 'EUR', name: 'Евро', symbol: '€' },
  { id: 'CNY', name: 'Юани', symbol: '¥' }
];

// Определение периодов
export const frequencyOptions = [
  { id: 'once', name: 'Разово' },
  { id: 'monthly', name: 'Ежемесячно' },
  { id: 'quarterly', name: 'Ежеквартально' },
  { id: 'annually', name: 'Ежегодно' }
];

// Определение типов капитализации
export const capitalizationOptions = [
  { id: 'none', name: 'Без капитализации' },
  { id: 'daily', name: 'Ежедневная' },
  { id: 'monthly', name: 'Ежемесячная' },
  { id: 'quarterly', name: 'Ежеквартальная' },
  { id: 'annually', name: 'Ежегодная' }
];

/**
 * DepositCalculatorPage Component - Полный калькулятор вкладов с капитализацией, пополнением и снятием
 */
const DepositCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('deposit-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор вкладов | Gensio';
  const metaDescription = tool?.metaDescription || 'Онлайн калькулятор вкладов: расчет доходности депозита с учетом капитализации, пополнений, снятий и налогов. Сравнение вкладов и построение графика накоплений для максимальной прибыли.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [showFullSchedule, setShowFullSchedule] = useState(false);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedDeposits, setSavedDeposits] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных вкладов из localStorage ---
  useEffect(() => {
    const savedDepositsData = localStorage.getItem('savedDeposits');
    if (savedDepositsData) {
      try {
        setSavedDeposits(JSON.parse(savedDepositsData));
      } catch (e) {
        console.error("Error loading saved deposits:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      const findRate = (id) => {
        const found = depositTypes.find(t => t.id === id);
        return found ? found.defaultRate.toString() : initialFormData.interestRate;
      };

      switch (subToolId) {
        case 'capitalization':
          newState.capitalization = 'monthly';
          break;
        case 'no-capitalization':
          newState.capitalization = 'none';
          break;
        case 'additional-contributions':
          newState.additionalContributions = true;
          break;
        case 'withdrawals':
          newState.withdrawals = true;
          break;
        case 'standard':
          newState.depositType = 'standard';
          newState.interestRate = findRate('standard');
          break;
        case 'profitable':
          newState.depositType = 'profitable';
          newState.interestRate = findRate('profitable');
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setSchedule([]);
    setErrors({});
    setShowFullSchedule(false);

  }, [subToolId]);

  // Сохранение вклада в localStorage
  const saveDeposit = (name) => {
    if (!results) return;

    const newSavedDeposit = {
      id: Date.now().toString(),
      name: name || `Вклад ${formData.depositAmount}${getCurrencySymbol(formData.currency)} на ${formData.depositTerm} мес.`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results },
      capitalization: formData.capitalization
    };

    const updatedSavedDeposits = [...savedDeposits, newSavedDeposit];
    setSavedDeposits(updatedSavedDeposits);
    localStorage.setItem('savedDeposits', JSON.stringify(updatedSavedDeposits));

    return newSavedDeposit;
  };

  // Получение символа валюты
  const getCurrencySymbol = (currencyCode) => {
    const currency = currencyOptions.find(c => c.id === currencyCode);
    return currency ? currency.symbol : '';
  };

  // Удаление сохраненного вклада
  const deleteSavedDeposit = (id) => {
    const updatedSavedDeposits = savedDeposits.filter(deposit => deposit.id !== id);
    setSavedDeposits(updatedSavedDeposits);
    localStorage.setItem('savedDeposits', JSON.stringify(updatedSavedDeposits));
  };

  // Загрузка сохраненного вклада
  const loadSavedDeposit = (id) => {
    const depositToLoad = savedDeposits.find(deposit => deposit.id === id);
    if (depositToLoad) {
      setFormData(depositToLoad.formData);
      setResults(depositToLoad.results);
      // Пересчитаем график для визуализации
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Coins className="w-8 h-8 text-green-600 dark:text-green-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор вкладов'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              schedule={schedule}
              setSchedule={setSchedule}
              depositTypes={depositTypes}
              frequencyOptions={frequencyOptions}
              capitalizationOptions={capitalizationOptions}
              currencyOptions={currencyOptions}
              saveDeposit={saveDeposit}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} formData={formData} currencyOptions={currencyOptions} />
                <DepositSchedule 
                  schedule={schedule} 
                  results={results}
                  formData={formData}
                  showFullSchedule={showFullSchedule}
                  setShowFullSchedule={setShowFullSchedule}
                  currencyOptions={currencyOptions}
                />
              </>
            )}

            {/* --- Информационный контент о вкладах --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
              currencyOptions={currencyOptions}
              capitalizationOptions={capitalizationOptions}
            />

            {/* --- Панель сравнения вкладов --- */}
            <ComparisonSidebar 
              savedDeposits={savedDeposits}
              deleteSavedDeposit={deleteSavedDeposit}
              loadSavedDeposit={loadSavedDeposit}
              currencyOptions={currencyOptions}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-green-600 focus:ring-green-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-green-600 focus:ring-green-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Scrollbar */
        .deposit-schedule-table {
          scrollbar-width: thin;
          scrollbar-color: theme('colors.gray.400') theme('colors.gray.100');
        }
        .dark .deposit-schedule-table {
          scrollbar-color: theme('colors.gray.600') theme('colors.gray.800');
        }
        .deposit-schedule-table::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .deposit-schedule-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.100');
          border-radius: 10px;
        }
        .dark .deposit-schedule-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.800');
        }
        .deposit-schedule-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.400');
          border-radius: 10px;
          border: 2px solid theme('colors.gray.100');
        }
        .dark .deposit-schedule-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.600');
          border-color: theme('colors.gray.800');
        }
        .deposit-schedule-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
        .dark .deposit-schedule-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
      `}</style>
    </div>
  );
};

export default DepositCalculatorPage;